import { useEffect, useState, type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { MonoLabel } from '@/components/editorial';
import { api } from '@/services/api';

export interface DisclosureGateProps {
  documentId: string;
  /** Rendered as-authored: no dash is prefixed, the same contract as PageHead. */
  eyebrow: string;
  title: string;
  explanation: string;
  consentLabel: string;
  onAccepted: () => void;
  /**
   * Called instead of onAccepted when consent was ALREADY recorded before this
   * gate mounted (e.g. the user revisits the step). Lets callers distinguish a
   * fresh, user-initiated agree (onAccepted) from a passive re-mount, so a
   * submit-on-accept gate doesn't silently resubmit. Defaults to onAccepted.
   */
  onAlreadyAccepted?: () => void;
  optional?: boolean;
  onSkip?: () => void;
  /**
   * #1009 / D-CS-12: render the pending gate WITHOUT asking the server whose
   * session this is. The copy editor's preview mounts this real component for
   * a staff user — their own consent status is not the family's, and a status
   * fetch that answered "accepted" would blank the very screen being edited.
   * The preview frame is inert, so the CTAs can never fire.
   */
  preview?: boolean;
  /**
   * #2133 item 5: a note rendered INSIDE the gate, above its heading — the
   * wizard passes `ConsentEnglishNotice` here. Rendered by the caller outside
   * the gate, it showed while the gate was still checking status and then
   * stayed on a step that had already passed, with nothing under it.
   */
  notice?: ReactNode;
}

type GateState = 'loading' | 'passed' | 'pending' | 'submitting';

export function DisclosureGate({
  documentId,
  eyebrow,
  title,
  explanation,
  consentLabel,
  onAccepted,
  onAlreadyAccepted,
  optional,
  onSkip,
  preview,
  notice,
}: DisclosureGateProps) {
  // #2109 R3 L7: only the gate's own chrome is here; eyebrow, title,
  // explanation and consentLabel arrive as props from the caller (R2's wizard).
  const { t } = useTranslation('consent');
  const { toast } = useToast();
  const [state, setState] = useState<GateState>(preview ? 'pending' : 'loading');
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (preview) return;
    let cancelled = false;
    api.consent.disclosureStatus(documentId).then((res) => {
      if (cancelled) return;
      if (res.data?.accepted) {
        setState('passed');
        // Passive re-mount, not a fresh user action — use onAlreadyAccepted so a
        // submit-on-accept caller doesn't auto-resubmit. Falls back to onAccepted.
        (onAlreadyAccepted ?? onAccepted)();
      } else {
        setState('pending');
      }
    }).catch(() => {
      if (!cancelled) setState('pending');
    });
    return () => { cancelled = true; };
    // onAccepted intentionally omitted — only run on mount / documentId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, preview]);

  const handleAgree = async () => {
    setState('submitting');
    try {
      await api.consent.recordDisclosure(documentId);
      onAccepted();
    } catch {
      toast({
        title: t('gate.recordFailed'),
        description: t('gate.recordFailedDescription'),
        variant: 'destructive',
      });
      setState('pending');
    }
  };

  // While checking status or already passed — render nothing (caller advances)
  if (state === 'loading' || state === 'passed') return null;

  return (
    <div
      className="border border-stevens-indigo/25 bg-stevens-indigo/5 rounded-xl p-4 space-y-4"
      role="region"
      aria-label={title}
    >
      {notice}

      {/* Shield icon + mono eyebrow */}
      <div className="flex items-start gap-3">
        <ShieldCheck
          className="mt-0.5 shrink-0 text-stevens-indigo"
          size={20}
          aria-hidden
        />
        {/* No dash prefix, matching PageHead — the label face, size and colour
            carry the eyebrow register on their own (design grammar v3.3). */}
        <MonoLabel tone="indigo">
          {eyebrow}
        </MonoLabel>
      </div>

      {/* Serif heading */}
      <h2
        className="font-serif text-xl font-normal leading-snug text-foreground m-0"
      >
        {title}
      </h2>

      {/* Italic-serif explanation in curly quotes */}
      <p
        className="font-serif text-body leading-relaxed text-stevens-ink-3 italic m-0"
      >
        {"“"}{explanation}{"”"}
      </p>

      {/* Consent checkbox */}
      <div className="flex items-start gap-2 pt-1">
        <Checkbox
          id={`disclosure-${documentId}`}
          checked={checked}
          onCheckedChange={(v) => setChecked(v === true)}
          disabled={state === 'submitting'}
        />
        <Label
          htmlFor={`disclosure-${documentId}`}
          className="text-body leading-snug cursor-pointer"
        >
          {consentLabel}
        </Label>
      </div>

      {/* CTAs */}
      <div className="flex items-center gap-3 pt-1">
        <Button
          onClick={handleAgree}
          disabled={!checked || state === 'submitting'}
          variant="brand"
        >
          {state === 'submitting' ? t('gate.recording') : t('gate.agree')}
        </Button>

        {optional && onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="text-body text-stevens-ink-3 underline underline-offset-2 hover:text-foreground transition-colors"
          >
            {t('gate.notNow')}
          </button>
        )}
      </div>
    </div>
  );
}
